/**
 * The console's API client. Same origin as the engine — no CORS, no cross-site cookies, and the frontend
 * can never be pointed at a different API version than the one it shipped with.
 *
 * The session token is held in memory and mirrored into sessionStorage so a page refresh does not throw
 * away a live session. sessionStorage (not localStorage) on purpose: it dies with the tab, which is the
 * right lifetime for a console that can see every tenant's money.
 */

const TOKEN_KEY = "bp.console.token";

let token: string | null = null;
try { token = sessionStorage.getItem(TOKEN_KEY); } catch { /* private mode — memory only */ }

export function setToken(t: string | null) {
  token = t;
  try { t ? sessionStorage.setItem(TOKEN_KEY, t) : sessionStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
}
export const getToken = () => token;

export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly code?: string) {
    super(message);
    this.name = "ApiError";
  }
}
/** The session died (expired, revoked, or the account was disabled). Callers sign the operator out. */
export class SessionExpired extends ApiError {
  constructor() { super("Your session has ended", 401, "console_session_invalid"); this.name = "SessionExpired"; }
}

async function request<T>(method: "GET" | "POST", path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers: {
        ...(token ? { authorization: `Console ${token}` } : {}),
        ...(body !== undefined ? { "content-type": "application/json" } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch {
    // A network failure is NOT an empty result. Screens must show "couldn't load", never "nothing here" —
    // on a money console those two read completely differently.
    throw new ApiError("Could not reach the payments engine", 0, "network");
  }

  if (res.status === 401 && token) { setToken(null); throw new SessionExpired(); }

  const text = await res.text();
  let json: unknown = null;
  try { json = text ? JSON.parse(text) : null; } catch { /* non-JSON body */ }

  if (!res.ok) {
    const e = json as { error?: string; code?: string; message?: string } | null;
    throw new ApiError(e?.message ?? e?.error ?? `Request failed (${res.status})`, res.status, e?.code ?? e?.error);
  }
  return json as T;
}

// ── Types mirroring the engine's responses (docs/CONSOLE_API.md) ────────────────────────────────────────

export interface ConsoleUser { id: string; email: string; name: string; role: "viewer" | "manager" | "super_admin" }
export type SignInResult =
  | { status: "enroll"; secret: string; uri: string }
  | { status: "totp_required" }
  | { status: "ok"; token: string; user: ConsoleUser };

export interface Overview {
  platformAccounts: Record<string, number>;
  tenantWallets: { count: number; totalOwed: number };
  custodyIdentity: { left: number; right: number; holds: boolean };
  attention: { deadLetters: number; parkedOutbox: number; openBreaks: number; payoutsInFlight: number };
  lastRecon: { ranAt: string; ok: boolean; providerKey: string } | null;
}

export interface MovementLine { account: string; tenantId: string | null; amount: number }
export interface Movement {
  id: string; kind: string; memo: string | null; refType: string | null; refId: string | null;
  reversesId: string | null; postedAt: string; lines: MovementLine[];
}
export interface WalletView {
  balance: number; currency: string; nextCursor: string | null;
  /** A cached LABEL from the engine. The tenantId stays the identity. */
  displayName?: string | null;
  statement: { id: string; amount: number; kind: string; refType: string | null; refId: string | null; memo: string | null; at: string }[];
}
export interface ReconRun { id: string; ranAt: string; providerKey: string; ok: boolean; summary: Record<string, unknown> }
export interface ReconBreak {
  id: string; runId: string; tenantId: string | null; description: string; amount: string | null;
  state: string; resolution: string | null; resolvedBy: string | null; resolvedAt: string | null; createdAt: string;
}
export interface Dlq {
  webhooks: { id: string; providerKey: string; providerEventId: string; kind: string | null; failReason: string | null; receivedAt: string }[];
  outbox: { id: string; kind: string; attempts: number; createdAt: string }[];
}
export interface FeeConfigRow {
  id: string; tenantId: string | null; rail: string; percentBps: number; flatKobo: number;
  capKobo: number | null; effectiveFrom: string; createdById: string | null; reason: string | null; createdAt: string;
}
export interface TeamMember {
  id: string; email: string; name: string; role: "viewer" | "manager" | "super_admin";
  isActive: boolean; enrolled: boolean; createdAt: string;
}
export interface AuditRow { id: string; userId: string; action: string; tenantId: string | null; detail: unknown; at: string }
export interface Flag {
  id: string; severity: 1 | 2 | 3; title: string; statement: string;
  money: number | null; action: string; target: string; targetId?: string | null; raisedAt: string;
}

export const api = {
  setupStatus: () => request<{ needed: boolean }>("GET", "/console/setup/status"),
  setup: (body: { token: string; email: string; name: string; password: string }) =>
    request<{ ok: boolean; email: string }>("POST", "/console/setup", body),

  signIn: (email: string, password: string, code?: string) =>
    request<SignInResult>("POST", "/console/auth/sign-in", { email, password, ...(code ? { code } : {}) }),
  confirmEnrollment: (email: string, password: string, code: string) =>
    request<{ token: string; user: ConsoleUser }>("POST", "/console/auth/enroll/confirm", { email, password, code }),
  signOut: () => request<{ signedOut: boolean }>("POST", "/console/auth/sign-out", {}),
  me: () => request<ConsoleUser>("GET", "/console/me"),

  overview: () => request<Overview>("GET", "/console/overview"),
  flags: () => request<{ flags: Flag[] }>("GET", "/console/flags"),

  movements: (q: Record<string, string | undefined>) =>
    request<{ movements: Movement[]; nextCursor: string | null; tenantNames: Record<string, string> }>("GET", `/console/movements${qs(q)}`),

  wallet: (tenantId: string, cursor?: string) =>
    request<WalletView>("GET", `/console/wallets/${encodeURIComponent(tenantId)}${qs({ cursor })}`),
  wallets: (q: Record<string, string | undefined>) =>
    request<{ wallets: { tenantId: string; displayName: string | null; balance: number; allowNegative: boolean; updatedAt: string }[]; nextCursor: string | null }>("GET", `/console/wallets${qs(q)}`),

  reconRuns: () => request<ReconRun[]>("GET", "/console/recon/runs"),
  reconBreaks: (state?: string) => request<ReconBreak[]>("GET", `/console/recon/breaks${qs({ state })}`),
  resolveBreak: (id: string, resolution: string) =>
    request<{ alreadyResolved: boolean }>("POST", `/console/recon/breaks/${encodeURIComponent(id)}/resolve`, { resolution }),

  dlq: () => request<Dlq>("GET", "/console/dlq"),
  replayWebhook: (id: string) => request<{ state: string; failReason?: string }>("POST", `/console/dlq/webhooks/${encodeURIComponent(id)}/replay`, {}),
  replayOutbox: (id: string) => request<{ requeued: boolean }>("POST", `/console/dlq/outbox/${encodeURIComponent(id)}/replay`, {}),

  fees: (tenantId?: string) => request<FeeConfigRow[]>("GET", `/console/fees${qs({ tenantId })}`),
  setFee: (body: { tenantId?: string | null; rail: string; percentBps: number; flatKobo: number; capKobo?: number | null; effectiveFrom?: string; reason: string }) =>
    request<FeeConfigRow>("POST", "/console/fees", body),

  consoleUsers: () => request<{ users: TeamMember[] }>("GET", "/console/users"),
  grantConsoleUser: (body: { email: string; name: string; password: string; role: string }) =>
    request<TeamMember>("POST", "/console/users", body),
  setConsoleUserActive: (id: string, isActive: boolean) =>
    request<{ ok: boolean }>("POST", `/console/users/${encodeURIComponent(id)}/active`, { isActive }),

  audit: (q: Record<string, string | undefined>) =>
    request<{ rows: AuditRow[]; nextCursor: string | null }>("GET", `/console/audit${qs(q)}`),
};

function qs(q: Record<string, string | undefined>): string {
  const parts = Object.entries(q).filter(([, v]) => v !== undefined && v !== "").map(([k, v]) => `${k}=${encodeURIComponent(v as string)}`);
  return parts.length ? `?${parts.join("&")}` : "";
}
